/**
 * Turnkey client configuration + active-network API helpers.
 *
 * The Turnkey constants (org ID / proxy / OAuth) are SINGLETONS — they
 * persist across the user's network toggle (single Turnkey org is a
 * hard architectural constraint; see lib/network/config.ts).
 *
 * Network-dependent helpers (`getSolanaChainId`, `getApiHeaders`,
 * `getApiUrl`, `getOauthRedirectUri`) read the current network from
 * `getActiveNetwork()` (the module-level singleton synced by
 * `NetworkSyncProvider`).
 */

import { env } from '@/lib/config/env';
import { getNetworkConfig } from '@/lib/network';
import { getActiveNetwork } from '@/lib/network/active';

export const TURNKEY_ORG_ID: string = env.NEXT_PUBLIC_TURNKEY_ORG_ID;
export const TURNKEY_API_BASE_URL: string = env.NEXT_PUBLIC_TURNKEY_API_BASE_URL;
export const TURNKEY_AUTH_PROXY_CONFIG_ID: string = env.NEXT_PUBLIC_TURNKEY_AUTH_PROXY_CONFIG_ID;

// Real X (Twitter) OAuth 2.0 client id, passed explicitly to the Turnkey
// provider. The auth proxy's `oauthClientIds.x` currently returns the Turnkey
// credential id (a UUID) instead of this value, which makes x.com reject the
// authorize request. Public, non-secret, network-independent (Turnkey is a
// singleton). Drop once the proxy publishes the credential's stored client id.
export const TURNKEY_X_CLIENT_ID = 'OVR0RDV6YjhVSEk2RWVIakJRU3c6MTpjaQ';

// ── Brand colors for Turnkey SDK UI config ──────────────────────────────────
// These hex values are required by @turnkey/react-wallet-kit's TurnkeyProviderConfig.
// Extracted from globals.css CSS custom properties.
// --color-brand-700: oklch(0.420 0.293 264) — core brand blue
export const TURNKEY_LIGHT_PRIMARY_HEX = '#0d20f2';
export const TURNKEY_DARK_PRIMARY_HEX = '#0d20f2';
// --radius: 0.625rem
export const TURNKEY_BORDER_RADIUS = '20px';

/** Backend API URL for the active network. */
export function getApiUrl(): string {
  return getNetworkConfig(getActiveNetwork()).apiUrl;
}

/** OAuth redirect URI for Turnkey OAuth (Google / Apple sign-in).
 *  Singleton — the same Vercel deployment serves both Solana networks
 *  via the cookie toggle, so OAuth providers only ever redirect to one
 *  callback URL regardless of which network the user has selected. */
export function getOauthRedirectUri(): string {
  return env.NEXT_PUBLIC_OAUTH_REDIRECT_URI;
}

/**
 * Turnkey CAIP-2 chain identifier for the active network.
 *
 * Turnkey's `handleSendTransaction` and `solSendTransaction` require a CAIP-2
 * chain identifier (`solana:devnet`, `solana:mainnet`) to target the correct
 * Solana cluster for sponsored transactions.
 *
 * @see https://docs.turnkey.com/networks/solana
 */
export function getSolanaChainId(): string {
  return getActiveNetwork() === 'mainnet' ? 'solana:mainnet' : 'solana:devnet';
}

/** Headers to send with backend API requests for the active network
 *  (X-API-Key when set). */
export function getApiHeaders(): Record<string, string> {
  const key = getNetworkConfig(getActiveNetwork()).apiKey;
  if (key !== undefined) {
    return { 'X-API-Key': key };
  }
  return {};
}
